import { Button } from "@/components";
import { useAsyncError, useWalletConnection, useSavedWallet } from "@/hooks";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { safeAsync } from "@/utils";
import { ConnectedAddress, PopupMenu } from "@/components";
import { type ReactNode, useState } from "react";

export function ConnectWalletButton() {
    const { disconnect, isConnected, address, ensName } = useWalletConnection();
    const { savedWallet } = useSavedWallet();
    const { openConnectModal } = useConnectModal();
    const throwError = useAsyncError();
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const handleDisconnect = async () => {
        setIsDisconnecting(true);
        try {
            const { error } = await safeAsync(disconnect);
            if (error) {
                throwError(error);
            }
        } finally {
            setIsDisconnecting(false);
        }
    };

    const handleConnect = () => {
        openConnectModal?.();
    };

    function renderConnectedWalletMenu(children: ReactNode) {
        return (
            <PopupMenu
                items={[
                    {
                        label: "Disconnect",
                        onClick: handleDisconnect,
                        icon: (
                            <div className="w-2 h-2 rounded-full bg-gray-500 transition-colors duration-300 mr-2 [.MuiMenuItem-root:hover_&]:bg-red-500" />
                        ),
                    },
                ]}
                className="relative"
                sx={{
                    "& .MuiPaper-root": {
                        backgroundColor: "white",
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "0.5rem",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    },
                    "& .MuiMenuItem-root": {
                        color: "rgba(156, 163, 175, 1)",
                        padding: "0.5rem 1rem",
                        transition: "all 0.3s",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            color: "rgb(var(--color-primary))",
                        },
                    },
                }}
            >
                <div className={`transition-all duration-300 ${isDisconnecting ? "opacity-50" : ""}`}>{children}</div>
            </PopupMenu>
        );
    }

    // Show connected wallet with disconnect option
    if (isConnected && address) {
        return renderConnectedWalletMenu(
            <ConnectedAddress ensName={ensName} address={address} className="text-dark" />,
        );
    }

    // Show saved wallet info when actual wallet is not connected but we have saved data
    if (savedWallet) {
        return renderConnectedWalletMenu(
            <ConnectedAddress ensName={savedWallet.ensName} address={savedWallet.address} className="text-dark" />,
        );
    }

    // Show connect button when no wallet is connected or saved
    return (
        <Button onClick={handleConnect} variant={"primary"} size="sm">
            Connect
        </Button>
    );
}
